/**
 * TrlExportModal — pick which parts of this game's design to download.
 *
 * The admin ticks sections (galaxy map, treasure list, each rule config, team
 * names) and gets one JSON bundle holding exactly those. A section this game has
 * nothing authored for is greyed out rather than hidden, so the list always
 * reads as the full menu of what a Trailblazers design can carry.
 *
 * Opened from the map-builder toolbar. Allowed on a locked map — exporting a
 * finished design to reuse elsewhere is the whole point.
 */

import { useMemo, useState } from 'react';
import { Modal } from '@swtorbit/ui/react';
import {
  TRL_BUNDLE_SECTIONS,
  summarizeTrlBundleSection,
  type TrlBundleSection,
} from '@swtorbit/shared-socket-events';
import {
  buildAvailableSections,
  buildBundleFile,
  downloadJson,
  type TrlBundleSources,
} from './trlBundle';
import { TrlBundleChecklist, type TrlBundleChecklistRow } from './TrlBundleChecklist';

interface TrlExportModalProps {
  sources: TrlBundleSources;
  onClose: () => void;
}

export function TrlExportModal({ sources, onClose }: TrlExportModalProps) {
  // Snapshot the game's state once on open: the checklist, the summaries and
  // the downloaded file all have to describe the same thing, and `sources` is a
  // fresh object on every parent render.
  const [available] = useState(() => buildAvailableSections(sources));

  const rows: TrlBundleChecklistRow[] = useMemo(
    () =>
      TRL_BUNDLE_SECTIONS.map((id) => ({
        id,
        available: available[id] !== undefined,
        summary: summarizeTrlBundleSection(id, available),
      })),
    [available],
  );

  // Everything the game can give is ticked by default — the common case is
  // "export the lot", and unticking is cheaper than hunting for each box.
  const [selected, setSelected] = useState<Set<TrlBundleSection>>(
    () => new Set(TRL_BUNDLE_SECTIONS.filter((id) => available[id] !== undefined)),
  );

  const toggle = (id: TrlBundleSection) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const doExport = () => {
    const file = buildBundleFile(available, selected, sources.roomCode, new Date().toISOString());
    downloadJson(file, `trl-bundle-${sources.roomCode}.json`);
    onClose();
  };

  const nothingToExport = rows.every((r) => !r.available);

  return (
    <Modal open onClose={onClose} className="trl-bundle-modal" title="Export Design" footer={
<>
        <div className="d-flex gap-xs" style={{ marginLeft: 'auto' }}>
          <button type="button" className="btn btn--outline btn--slate" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--green"
            onClick={doExport}
            disabled={selected.size === 0}
          >
            Export {selected.size > 0 ? `(${selected.size})` : ''}
          </button>
        </div>
      </>
}>
        <div className="d-flex flex-column gap-sm">
          <p className="fs-xs text-slate m-0">
            Choose what to save into the bundle file. Screenshots and cartel rewards travel as
            references, not copies — importing elsewhere re-links them to that admin&apos;s own
            library, and names anything it can&apos;t find.
          </p>

          {nothingToExport ? (
            <p className="fs-sm text-slate m-0">
              This game has nothing to export yet — add some planets or settings first.
            </p>
          ) : (
            <TrlBundleChecklist
              rows={rows}
              selected={selected}
              onToggle={toggle}
              onSelectAll={() =>
                setSelected(new Set(TRL_BUNDLE_SECTIONS.filter((id) => available[id] !== undefined)))
              }
              onSelectNone={() => setSelected(new Set())}
            />
          )}
        </div>
      </Modal>
  );
}
